import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";

const DanaAvailabilityCalendar = ({
  mealType = null,
  bookedDates = [],
  blockedDates = [],
  onDateSelect,
  selectedDate: controlledSelectedDate = null,
  showLegend = true,
  customStyles = {},
  onDateClick,
  showRefreshButton = true,
  onRefresh,
  maxDate: propMaxDate = null,
}) => {
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [bookedDatesSet, setBookedDatesSet] = useState(new Set());
  const [blockedDatesSet, setBlockedDatesSet] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const maxDate = propMaxDate || dayjs().add(1, 'year').format('YYYY-MM-DD');
  const selectedDate = controlledSelectedDate || internalSelectedDate;

  // Process external data
  useEffect(() => {
    const processExternalData = () => {
      try {
        const bookedSet = new Set();
        const blockedSet = new Set();

        // Process booked dates
        bookedDates.forEach((booking) => {
          const eventDate = booking.dana_event_date || booking.date;
          if (eventDate) {
            const dateKey = extractDateKey(eventDate);
            if (dateKey) {
              // Filter by meal type if specified
              if (!mealType || booking.meal_type === mealType) {
                const status = (booking.status || '').toLowerCase();
                const shouldShow = status === 'approved' || status === 'pending' || status === 'completed';
                
                if (shouldShow) {
                  bookedSet.add(dateKey);
                }
              }
            }
          }
        });

        // Process blocked dates
        blockedDates.forEach((blocked) => {
          const eventDate = blocked.dana_event_date || blocked.date;
          if (eventDate) {
            const dateKey = extractDateKey(eventDate);
            if (dateKey) {
              // Filter by meal type if specified
              if (!mealType || !blocked.meal_type || blocked.meal_type === mealType) {
                blockedSet.add(dateKey);
              }
            }
          }
        });

        setBookedDatesSet(bookedSet);
        setBlockedDatesSet(blockedSet);
        setError(null);
      } catch (err) {
        console.error("Error processing calendar data:", err);
        setError("Failed to process calendar data.");
      }
    };

    processExternalData();
  }, [bookedDates, blockedDates, mealType]);

  const extractDateKey = (dateString) => {
    try {
      if (!dateString) return null;
      
      if (typeof dateString === 'string') {
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }
        if (dateString.includes('T')) {
          return dateString.split('T')[0];
        }
      }
      
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      return null;
    } catch (error) {
      console.error("Error extracting date key:", error);
      return null;
    }
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (err) {
        console.error("Error refreshing:", err);
        setError("Failed to refresh data.");
      } finally {
        setRefreshing(false);
      }
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, "month"));
  };

  const isPastDate = (dateString) => {
    const today = dayjs().startOf('day');
    const date = dayjs(dateString).startOf('day');
    return date.isBefore(today);
  };

  const isBeyondMaxDate = (dateString) => {
    const max = dayjs(maxDate).startOf('day');
    const date = dayjs(dateString).startOf('day');
    return date.isAfter(max, 'day');
  };

  const handleDateClick = (dateString) => {
    if (isPastDate(dateString)) {
      return;
    }

    // Check if date is booked
    if (bookedDatesSet.has(dateString)) {
      return;
    }

    // Check if date is already blocked
    if (blockedDatesSet.has(dateString)) {
      return;
    }

    if (onDateClick) {
      onDateClick(dateString);
    }

    if (!controlledSelectedDate) {
      setInternalSelectedDate(dateString);
    }

    if (onDateSelect) {
      onDateSelect(dateString, dayjs(dateString));
    }
  };

  const isDateBooked = (dateString) => {
    return bookedDatesSet.has(dateString);
  };

  const isDateBlocked = (dateString) => {
    return blockedDatesSet.has(dateString);
  };

  const isDateDisabled = (dateString) => {
    if (isPastDate(dateString)) return true;
    if (isBeyondMaxDate(dateString)) return true;
    return isDateBooked(dateString) || isDateBlocked(dateString);
  };

  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startOfCalendar = startOfMonth.startOf("week");
    const endOfCalendar = endOfMonth.endOf("week");

    const days = [];
    let currentDay = startOfCalendar;

    while (
      currentDay.isBefore(endOfCalendar) ||
      currentDay.isSame(endOfCalendar, "day")
    ) {
      days.push(currentDay);
      currentDay = currentDay.add(1, "day");
    }

    return days;
  };

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const calendarDays = generateCalendarDays();

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={
          <button onClick={handleRefresh} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            Retry
          </button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        padding: "18px",
        backgroundColor: "#fff",
        width: "400px", // Fixed width
        ...customStyles,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="364px">
        <IconButton onClick={handlePrevMonth} size="small">
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        
        <Typography
          variant="h6"
          sx={{ 
            textAlign: "center",
            fontSize: "1.1rem",
            fontWeight: 500,
            flex: 1,
            mx: 1
          }}
        >
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        
        <IconButton onClick={handleNextMonth} size="small">
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        
        {showRefreshButton && onRefresh && (
          <IconButton 
            onClick={handleRefresh} 
            disabled={refreshing}
            size="small"
            title="Refresh calendar"
            sx={{
              ml: 1,
              animation: refreshing ? 'spin 0.5s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {refreshing && (
        <Box display="flex" justifyContent="center" mb={2}>
          <CircularProgress size={24} />
          <Typography variant="body2" ml={1}>
            Refreshing...
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
          width: "364px",
          height: "364px",
        }}
      >
        {weekDays.map((day, index) => (
          <Box
            key={day}
            sx={{
              width: "52px",
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              fontFamily: "Arial",
              fontSize: "14px",
              color: "black",
              margin: 0,
              padding: "8px",
              marginLeft: index > 0 ? "-1px" : 0,
            }}
          >
            {day}
          </Box>
        ))}

        {calendarDays.map((day) => {
          const dateString = day.format("YYYY-MM-DD");
          const isBooked = isDateBooked(dateString);
          const isBlocked = isDateBlocked(dateString);
          const isPast = isPastDate(dateString);
          const isDisabled = isDateDisabled(dateString);
          const isSelected = selectedDate === dateString;

          // Determine cell style based on date state
          let backgroundColor = "#fff";
          let color = "black";
          let borderStyle = "1px solid #ddd";
          let cursor = "pointer";
          
          if (isSelected) {
            backgroundColor = "#c49b36";
            color = "white";
          } else if (isPast || isBeyondMaxDate(dateString)) {
            backgroundColor = "#f0f0f0";
            color = "#ccc";
            cursor = "not-allowed";
          } else if (isBlocked) {
            backgroundColor = "#ffebee";
            color = "#d32f2f";
            cursor = "not-allowed";
          } else if (isBooked) {
            backgroundColor = "#fff3e0";
            color = "#666";
            cursor = "not-allowed";
            borderStyle = "2px solid #f44336";
          }

          if (isDisabled && !isPast) {
            cursor = "not-allowed";
          }

          return (
            <Box
              key={dateString}
              onClick={() => !isDisabled && handleDateClick(dateString)}
              sx={{
                width: "52px",
                height: "52px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                cursor: cursor,
                border: borderStyle,
                borderRadius: "0",
                margin: 0,
                fontFamily: "Arial",
                padding: "8px",
                marginTop: "-1px",
                marginLeft: "-1px",
                backgroundColor: backgroundColor,
                color: color,
                opacity: isDisabled ? 0.7 : 1,
                "&:hover": {
                  backgroundColor: isDisabled
                    ? backgroundColor
                    : isSelected
                    ? "#c49b36"
                    : "#f4e4a1",
                },
              }}
            >
              {day.date()}
            </Box>
          );
        })}
      </Box>

      {showLegend && (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          gap={1}
          alignItems="flex-start"
          width="100%"
          sx={{ pl: 1 }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: "transparent",
                border: "2px solid #f44336",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">
              <strong>Booked</strong> - Already has dana booking (cannot be blocked)
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: "#ffebee",
                border: "1px solid #d32f2f",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">
              <strong>Blocked</strong> - Manually blocked by coordinator (unavailable)
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">
              <strong>Available</strong> - Click to block this date for dana
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mt={1}>
            <Typography variant="caption" color="textSecondary">
              📅 Only dates within one year from today can be blocked.
            </Typography>
          </Box>
          
        </Box>
      )}
    </Box>
  );
};

export default DanaAvailabilityCalendar;